/* Foliage Factory — Salepify cart compatibility V9.
   Cart-line identity and checkout totals come from Shopify; offer eligibility
   remains owned by Salepify. This is a storefront safety net, not a server-side
   checkout validator. Optional removals apply only to the current user action.
*/
(() => {
  'use strict';
  if (window.FF_GIFT_CART_V9) return;
  window.FF_GIFT_CART_V9 = true;

  const isGerman = (document.documentElement.lang || '').toLowerCase().startsWith('de');
  const translation = window.themeVariables?.strings || {};
  const fallback = isGerman ? {
    failed: 'Das Aktionsgeschenk konnte nicht entfernt werden. Bitte versuche es erneut.',
    invalid: 'Ein Aktionsgeschenk ist nicht mehr kostenlos. Wir konnten es nicht entfernen. Bitte versuche es erneut, bevor du zur Kasse gehst.',
    changed: 'Dein Warenkorb wurde aktualisiert. Bitte prüfe die Artikel und den Gesamtbetrag, bevor du zur Kasse gehst.',
    retry: 'Erneut versuchen',
    review: 'Ein Aktionsgeschenk wird mit einem Preis angezeigt. Prüf bitte deinen Warenkorb, bevor du zur Kasse gehst.',
  } : {
    failed: 'We could not remove this promotional gift. Please try again.',
    invalid: 'A promotional gift is no longer free, and we could not remove it. Please try again before checkout.',
    changed: 'Your cart has been updated. Please review the items and total before checkout.',
    retry: 'Try again',
    review: 'A promotional gift currently has a price. Review or remove it before checkout.',
  };
  const strings = {
    failed: translation.ffGiftRemoveFailed || fallback.failed,
    invalid: translation.ffGiftInvalid || fallback.invalid,
    changed: translation.ffGiftChanged || fallback.changed,
    retry: translation.ffGiftRetry || fallback.retry,
    review: translation.ffGiftReview || fallback.review,
  };

  const giftProperty = Object.freeze({
    progress: '_salepify-progress-bar', advanced: '_salepify-advanced-fg',
    embed: '_salepify-embed', bogo: '_salepify-bogo',
    discount: '_salepify-discount-code-fg'
  });
  const oldAnthuriums = Object.freeze({
    '6601-0': 61185556152650, // magnificum
    '6601-1': 53298660147530, // queremalense
    '6601-2': 61189896831306  // luxurians
  });
  const mintGift = Object.freeze({marker: '25941', variant: 53298840142154,
    expiresAt: Date.parse('2026-11-01T23:00:00Z')});
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const feedbackKey = 'ff-gift-v8-feedback';
  const CLEANUP_BACKOFF = 6000;
  const POLL_INTERVAL = 4000;
  const CART_REQUEST_DEADLINE_MS = 8000;
  const ERROR_TEXT_SELECTOR = '.ff-product-form-error p, #error-announcement, .banner--error p';
  const GIFT_LABEL_SELECTOR = '.salepify-popup__claimed-label, form.cart-page .discount-badge, cart-drawer .discount-badge';
  const alocasiaTitle = /^\s*Up to 5 Alocasias? for free(?=\s*(?:\(|$))/i;
  const alocasiaRemoveLabel = /^Up to 5 Alocasias? for free(?= entfernen$)/i;
  const giftClaimedLabel = translation.ffGiftClaimed || 'Hinzugefügt';
  const alocasiaPromotionLabel = translation.ffAlocasiaGiftPromotion || 'Bis zu 5 Alocasien gratis';

  function localizeGiftLabel(element) {
    if (!isGerman || !element) return;
    if (element.matches('.salepify-popup__claimed-label')) {
      if (!element.childElementCount && element.textContent.trim() === 'Claimed') {
        element.textContent = giftClaimedLabel;
      }
      return;
    }
    for (const node of element.childNodes) {
      if (node.nodeType !== Node.TEXT_NODE) continue;
      const value = node.textContent.replace(alocasiaTitle, alocasiaPromotionLabel);
      if (value !== node.textContent) node.textContent = value;
    }
    element.querySelectorAll('cart-discount-remove-button button[aria-label]').forEach(button => {
      const original = button.getAttribute('aria-label');
      const localized = original.replace(alocasiaRemoveLabel, alocasiaPromotionLabel);
      if (localized !== original) button.setAttribute('aria-label', localized);
    });
  }
  // A newly added free gift may briefly display its regular price before the app discount settles.
  // Price alone is not proof that a promotion has expired: an app discount can settle later.
  // Never automatically remove an unverified gift just because it is momentarily priced.
  let memoryFeedback = null;
  const busyGifts = new Set();
  let currentCart = null;
  let cleanupPromise = null;
  let inspectionPromise = null;
  let checkTimer = null;
  let lastRefresh = 0;
  let lastFailedCleanup = 0;
  let lastSurface = null;
  let failedRemoval = null;

  const isColdProtection = g => String(g?.variant || '') === '61211921875274';
  const lineId = (line) => Number(line?.id || line?.variant_id);
  const identity = (variant, type, marker) => ({variant: String(variant), type: String(type), marker: String(marker)});
  const identityKey = g => `${g.type}:${g.marker}:${g.variant}`;
  function fromControl(el) {
    if (!el) return null;
    const g = identity(el.dataset.ffGiftVariant || '', el.dataset.ffGiftMarkerType || '', el.dataset.ffGiftCampaign || '');
    return g.variant && giftProperty[g.type] && g.marker ? g : null;
  }
  function lineMatches(line, g) {
    return Boolean(line && g && lineId(line) === Number(g.variant) &&
      String(line.properties?.[giftProperty[g.type]] ?? '') === g.marker);
  }
  function findLine(cart, g, preferredKey = '') {
    const matches = (cart?.items || []).filter(line => lineMatches(line, g));
    return matches.find(line => line.key === preferredKey) || matches[0] || null;
  }
  function fromLine(line) {
    if (!line) return null;
    for (const [type, prop] of Object.entries(giftProperty)) {
      const marker = line.properties?.[prop];
      if (marker != null && String(marker).trim()) return identity(lineId(line), type, marker);
    }
    return null;
  }
  function lineIsCharged(line) {
    const total = line?.final_line_price ?? line?.line_price;
    if (total != null && Number.isFinite(Number(total))) return Number(total) > 0;
    const unit = line?.final_price ?? line?.price;
    return unit != null && Number.isFinite(Number(unit)) && Number(unit) > 0;
  }
  function expiredGift(line, g = fromLine(line)) {
    // The old Anthurium campaign is confirmed closed. A Mint gift is expired only
    // after its recorded end date AND when Shopify actually shows a charge.
    // A client clock cannot by itself invalidate a free item.
    return Boolean(g && ((g.type === 'progress' && oldAnthuriums[g.marker] === lineId(line)) ||
      (g.type === 'advanced' && g.marker === mintGift.marker && lineId(line) === mintGift.variant &&
        Date.now() >= mintGift.expiresAt && lineIsCharged(line))));
  }
  function chargedGiftCandidate(line, g = fromLine(line)) {
    // Only Salepify's free-gift markers; BOGO may represent legitimately paid items.
    return Boolean(g && ['advanced', 'progress', 'discount', 'embed'].includes(g.type) && lineIsCharged(line));
  }
  // Known-ended campaign markers may be cleaned automatically. All other marked
  // items showing a positive final price require customer review, never a timer.
  function invalidGift(line) {return expiredGift(line);}
  const hasInvalid = cart => Boolean(cart?.items?.some(invalidGift));
  const hasPricedGift = cart => Boolean(cart?.items?.some(line =>
    chargedGiftCandidate(line) && !expiredGift(line)));
  function invalidRendered() {
    return [...document.querySelectorAll('quantity-selector[data-ff-gift-campaign]')]
      .some(el => {
        const g = fromControl(el);
        return Boolean(g && g.type === 'progress' && oldAnthuriums[g.marker] === Number(g.variant));
      });
  }

  const storage = (() => {try {return window.sessionStorage;} catch (_) {return null;}})();
  try {
    const legacyKeys = [];
    for (let i = 0; storage && i < storage.length; ++i) {
      const key = storage.key(i);
      if (key?.startsWith('ff-gift-v7-declined:') || key === 'ff-gift-v7-feedback') legacyKeys.push(key);
    }
    legacyKeys.forEach(key => storage.removeItem(key));
  } catch (_) {}
  function stashMessage(code, g = null) {
    memoryFeedback = {code, g, at: Date.now()};
    try {storage?.setItem(feedbackKey, JSON.stringify(memoryFeedback));} catch (_) {}
  }
  function takeMessage() {
    let value = memoryFeedback;
    try {value = JSON.parse(storage?.getItem(feedbackKey) || 'null') || value; storage?.removeItem(feedbackKey);} catch (_) {}
    memoryFeedback = null;
    return value && Date.now() - value.at < 20000 ? value : null;
  }

  // Put a deadline around both fetch and response parsing. Shopify/Salepify
  // network failures must not leave controls indefinitely busy. A timed-out
  // mutation has an unknown outcome; callers must read Shopify's current cart
  // before retrying rather than assume the removal failed.
  async function cartRequest(path, options) {
    const controller = new AbortController();
    let timer;
    const attempt = (async () => {
      const response = await fetch(root + path, {...options, signal: controller.signal});
      const body = await response.json();
      return {response, body};
    })();
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Cart request timed out'));
      }, CART_REQUEST_DEADLINE_MS);
    });
    try {return await Promise.race([attempt, deadline]);}
    finally {clearTimeout(timer);}
  }
  async function readCart() {
    const {response, body} = await cartRequest('cart.js', {
      credentials: 'same-origin', cache: 'no-store',
      headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'}
    });
    if (!response.ok || !Array.isArray(body?.items)) throw new Error('Cart read failed');
    currentCart = body;
    return body;
  }
  async function removeLine(key) {
    const {response, body} = await cartRequest('cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      body: JSON.stringify({id: String(key), quantity: 0})
    });
    if (!response.ok || !Array.isArray(body?.items)) throw new Error('Cart removal failed');
    currentCart = body;
    return body;
  }
  function fromDrawer(node) {return Boolean(node?.closest?.('#cart-drawer, cart-drawer'));}
  function feedbackHost(source = null) {
    const drawer = document.querySelector('#cart-drawer .cart-drawer__footer, cart-drawer .cart-drawer__footer');
    const page = document.querySelector('form.cart-page .cart-footer');
    if (source && fromDrawer(source)) return drawer || page;
    if (source) return page || drawer;
    const openDrawer = document.querySelector('cart-drawer[open], #cart-drawer[aria-hidden="false"], cart-drawer.is-open');
    return (openDrawer && drawer) || page || drawer;
  }
  function notice(content, retry = false, source = null, retryAction = null, kind = '') {
    const host = feedbackHost(source || lastSurface);
    if (!host) return;
    let box = host.querySelector(':scope > [data-ff-gift-feedback]');
    if (!box) {box = document.createElement('div');box.dataset.ffGiftFeedback = '';
      box.className = 'ff-gift-removal-notice text-sm';box.setAttribute('role', 'alert');
      box.style.marginBlock = '0.75rem';box.style.color = 'var(--error-text-color, #a32626)';
      host.prepend(box);
    }
    box.dataset.ffGiftNoticeKind = kind;
    box.replaceChildren();
    const span = document.createElement('span');span.textContent = content;box.append(span);
    if (retry) {const button = document.createElement('button');button.type = 'button';
      button.className = 'link text-sm';button.style.marginInlineStart = '0.75rem';
      button.dataset.ffGiftRetry = '';button.textContent = strings.retry;
      if (typeof retryAction === 'function') {
        button.addEventListener('click', event => {
          event.preventDefault();
          if (!button.isConnected) return;
          button.disabled = true;
          Promise.resolve().then(retryAction).catch(() => {}).finally(() => {
            if (button.isConnected) button.disabled = false;
          });
        });
      } else button.disabled = true; // Never display a control with no action.
      box.append(button);}
  }
  function clearNotice(source = null) {
    const host = feedbackHost(source || lastSurface);
    host?.querySelector(':scope > [data-ff-gift-feedback]')?.remove();
  }
  function freshPage() {
    if (Date.now() - lastRefresh < 1000) return;
    lastRefresh = Date.now();
    window.location.reload();
  }
  function isZeroLink(link) {
    try {const url = new URL(link.href, location.href);
      return url.origin === location.origin && /\/cart\/change\/?$/.test(url.pathname) &&
        url.searchParams.get('quantity') === '0';} catch (_) {return false;}
  }
  function giftControl(link) {
    return link.closest('line-item-quantity')?.querySelector('quantity-selector[data-ff-gift-campaign][data-ff-gift-variant]');
  }

  async function onRemove(g, key, control, source) {
    if (isColdProtection(g)) return;
    const id = identityKey(g);
    if (busyGifts.size) return;
    busyGifts.add(id);
    failedRemoval = null;
    clearNotice(source);
    lastSurface = source;
    control?.setAttribute('aria-busy', 'true');
    try {
      const before = await readCart();
      const item = findLine(before, g, key);
      if (!item) {freshPage();return;}
      const result = await removeLine(item.key);
      if (findLine(result, g)) throw new Error('Gift removal not confirmed');
      freshPage();
    } catch (_) {
      try {
        const cart = await readCart();
        if (!findLine(cart, g)) {clearNotice(source);freshPage();return;}
      } catch (_) {}
      failedRemoval = {g, source};
      notice(strings.failed, true, source,
        () => onRemove(g, '', control?.isConnected ? control : null, source), 'removal');
    } finally {busyGifts.delete(id);control?.removeAttribute('aria-busy');}
  }
  document.addEventListener('click', event => {
    const link = event.target?.closest?.('a[href]');
    if (!link || !isZeroLink(link)) return;
    const control = giftControl(link);
    const g = fromControl(control);
    if (!g) return; // normal paid items use Shopify/Prestige native handlers
    event.preventDefault();event.stopImmediatePropagation();
    onRemove(g, control.dataset.ffGiftLineKey, control, link).catch(() => {});
  }, true);

  // Decode customer-visible error strings as text, without changing their meaning.
  const entities = {amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', nbsp: ' '};
  function decode(value) {
    let result = String(value ?? '');
    for (let pass = 0; pass < 2; ++pass) {
      const next = result.replace(/&(?:#x([0-9a-f]{1,6})|#(\d{1,7})|(amp|quot|apos|lt|gt|nbsp));/gi,
        (full, hex, dec, named) => {if (named) return entities[named.toLowerCase()] || full;
          const n = parseInt(hex || dec, hex ? 16 : 10);
          return n > 0 && n <= 0x10ffff && !(n >= 0xd800 && n <= 0xdfff) ? String.fromCodePoint(n) : full;});
      if (next === result) break;
      result = next;
    }
    return result;
  }
  function repairError(node) {
    if (!(node instanceof Element) || !node.matches(ERROR_TEXT_SELECTOR)) return;
    const walk = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    let textNode;
    while ((textNode = walk.nextNode())) {
      const value = decode(textNode.nodeValue);
      if (value !== textNode.nodeValue) textNode.nodeValue = value;
    }
  }

  async function cleanupInvalid(force = false, source = null) {
    if (cleanupPromise) return cleanupPromise;
    if (!force && Date.now() - lastFailedCleanup < CLEANUP_BACKOFF) return false;
    cleanupPromise = (async () => {
      let changed = false;
      try {
        let cart = await readCart();
        for (let pass = 0; pass < 2; ++pass) {
          const bad = cart.items.filter(invalidGift);
          if (!bad.length) {
            if (changed) {stashMessage('changed');freshPage();}
            else if (invalidRendered()) freshPage();
            return true;
          }
          for (const stale of bad) {
            cart = await readCart();
            const g = fromLine(stale);
            const current = findLine(cart, g, stale.key);
            if (current && invalidGift(current)) {await removeLine(current.key);changed = true;}
          }
          cart = await readCart();
        }
        if (hasInvalid(cart)) {lastFailedCleanup = Date.now();notice(strings.invalid, true, source,
          () => cleanupInvalid(true, source));return false;}
        if (changed) {stashMessage('changed');freshPage();}
        return true;
      } catch (_) {lastFailedCleanup = Date.now();notice(strings.invalid, true, source,
        () => cleanupInvalid(true, source));return false;}
    })();
    try {return await cleanupPromise;} finally {cleanupPromise = null;}
  }

  // Prestige's existing checkout submit validator is the sole form handler.
  // Do not add an asynchronous submit-capture/replay handler here: two owners
  // create an infinite requestSubmit/submitter.click revalidation loop.
  // Known expired gifts are cleaned on cart events; other price-bearing
  // promotional lines are shown for review, not silently deleted.

  async function inspect() {
    const cart = await readCart();
    if (hasInvalid(cart)) {await cleanupInvalid();return;}
    if (invalidRendered()) {freshPage();return;}
    if (failedRemoval && !findLine(cart, failedRemoval.g)) {
      clearNotice(failedRemoval.source);
      failedRemoval = null;
    }
    // This warning is advisory. Prestige's existing checkout validator remains
    // the sole submit owner; genuine checkout enforcement needs an app/server
    // rule, not another theme-level replay listener.
    if (!failedRemoval && hasPricedGift(cart)) {
      notice(strings.review, true, null, () => inspect(), 'review');
    } else if (!hasPricedGift(cart)) {
      document.querySelectorAll('[data-ff-gift-feedback][data-ff-gift-notice-kind="review"]')
        .forEach(box => box.remove());
    }
  }
  function scheduleCheck() {
    if (checkTimer) return;
    checkTimer = setTimeout(() => {checkTimer = null;
      if (inspectionPromise) return;
      inspectionPromise = inspect().catch(() => {}).finally(() => {inspectionPromise = null;});
    }, 180);
  }
  const observer = new MutationObserver(mutations => {
    let needsCheck = false;
    for (const mutation of mutations) {
      const labelHost = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      localizeGiftLabel(labelHost?.closest(GIFT_LABEL_SELECTOR));
      if (mutation.type === 'characterData') {
        const ancestor = mutation.target.parentElement?.closest(ERROR_TEXT_SELECTOR);
        if (ancestor) repairError(ancestor);
        continue;
      }
      // Setting an existing element's textContent replaces its Text child.
      // MutationObserver reports a childList mutation with an added Text node,
      // not characterData; decode the mutation target before examining children.
      const host = mutation.target instanceof Element
        ? mutation.target.closest(ERROR_TEXT_SELECTOR) : null;
      if (host) repairError(host);
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (isGerman) {
          if (node.matches(GIFT_LABEL_SELECTOR)) localizeGiftLabel(node);
          node.querySelectorAll(GIFT_LABEL_SELECTOR).forEach(localizeGiftLabel);
        }
        if (node.matches(ERROR_TEXT_SELECTOR)) repairError(node);
        node.querySelectorAll?.(ERROR_TEXT_SELECTOR).forEach(repairError);
        if (node.matches('quantity-selector[data-ff-gift-campaign], form.cart-page, cart-drawer') ||
            node.querySelector?.('quantity-selector[data-ff-gift-campaign], form.cart-page, cart-drawer')) needsCheck = true;
      }
    }
    if (needsCheck) scheduleCheck();
  });
  observer.observe(document.documentElement, {subtree: true, childList: true, characterData: true});
  document.addEventListener('cart:change', scheduleCheck);
  document.addEventListener('cart:updated', scheduleCheck);
  document.addEventListener('cart:refresh', scheduleCheck);
  window.addEventListener('pageshow', scheduleCheck);
  const poll = setInterval(() => {
    const cartPage = /\/cart\/?$/.test(location.pathname);
    const drawer = document.querySelector('#cart-drawer');
    const drawerOpen = Boolean(drawer?.hasAttribute('open') ||
      drawer?.getAttribute('aria-hidden') === 'false' || drawer?.classList.contains('is-open'));
    const hasGiftControls = Boolean(document.querySelector('quantity-selector[data-ff-gift-campaign]'));
    if ((cartPage || drawerOpen) && document.visibilityState !== 'hidden' &&
        (hasGiftControls || failedRemoval)) scheduleCheck();
  }, POLL_INTERVAL);
  window.addEventListener('pagehide', () => clearInterval(poll), {once: true});

  const confirmedGiftAdds = new Map();
  const confirmedGiftApps = new WeakSet();
  let giftRefreshInstalled = false;

  function giftRequestItems(items) {
    if (!Array.isArray(items) || !items.length) return null;
    const requests = new Map();
    for (const item of items) {
      const g = fromLine(item);
      if (!g || g.type === 'bogo' || isColdProtection(g) || item.selling_plan ||
          !Number.isSafeInteger(Number(item.id)) || !Number.isSafeInteger(item.quantity) || item.quantity <= 0) return null;
      const properties = Object.entries(item.properties || {}).map(([key, value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b));
      const key = JSON.stringify([String(item.id), properties]);
      const request = requests.get(key) || {id: Number(item.id), properties, quantity: 0};
      request.quantity += item.quantity;
      requests.set(key, request);
    }
    return [...requests.values()];
  }
  function requestedGiftLines(cart, request) {
    return cart.items.filter(line => lineId(line) === request.id && !line.selling_plan_allocation &&
      JSON.stringify(Object.entries(line.properties || {}).map(([key, value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b))) === JSON.stringify(request.properties));
  }
  function giftsConfirmedFree(before, after, requests) {
    if (!Array.isArray(before?.items) || !Array.isArray(after?.items)) return false;
    return requests.every(request => {
      const previous = requestedGiftLines(before, request);
      const added = requestedGiftLines(after, request);
      const quantity = lines => lines.reduce((total, line) => total + line.quantity, 0);
      return [...previous, ...added].every(line => Number.isSafeInteger(line.quantity) && line.quantity > 0) &&
        quantity(added) === quantity(previous) + request.quantity &&
        added.every(line => typeof line.final_line_price === 'number' && line.final_line_price === 0);
    });
  }
  function installGiftConfirmation() {
    if (!giftRefreshInstalled && typeof window.tlGetRefreshCartFunc === 'function') {
      const originalFactory = window.tlGetRefreshCartFunc;
      window.tlGetRefreshCartFunc = callback => async () => {
        const updates = [];
        document.dispatchEvent(new CustomEvent('cart:refresh', {detail: {updates}}));
        if (!updates.length) return originalFactory(callback)();
        try {
          await Promise.all(updates);
          await callback();
        } catch (error) {
          freshPage();
          throw error;
        }
      };
      giftRefreshInstalled = true;
    }
    const app = window.tlFreeGiftOffer;
    if (!giftRefreshInstalled || !app || confirmedGiftApps.has(app) || typeof app.handleAddGifts !== 'function') return;
    confirmedGiftApps.add(app);
    app.refreshCartFunc = null;
    const originalAdd = app.handleAddGifts;
    app.handleAddGifts = function(items, ...args) {
      const requests = giftRequestItems(items);
      if (!requests) return originalAdd.call(this, items, ...args);
      const key = JSON.stringify([requests, args]);
      if (confirmedGiftAdds.has(key)) return confirmedGiftAdds.get(key);
      const operation = (async () => {
        let before;
        try {before = await readCart();} catch (_) {}
        const result = await originalAdd.call(this, items, ...args);
        if (result?.discountError === true && before) {
          try {
            const after = await readCart();
            if (giftsConfirmedFree(before, after, requests)) return {status: 200};
          } catch (_) {}
        }
        return result;
      })();
      confirmedGiftAdds.set(key, operation);
      operation.finally(() => confirmedGiftAdds.delete(key)).catch(() => {});
      return operation;
    };
  }
  document.addEventListener('click', installGiftConfirmation, true);
  window.addEventListener('load', installGiftConfirmation, {once: true});

  async function initialize() {
    installGiftConfirmation();
    if (isGerman) document.querySelectorAll(GIFT_LABEL_SELECTOR).forEach(localizeGiftLabel);
    document.querySelectorAll('.ff-product-form-error p, #error-announcement, .banner--error p').forEach(repairError);
    if (location.hash === '#ff-gift-review') {
      try {history.replaceState(history.state, '', location.pathname + location.search);} catch (_) {}
    }
    const previous = takeMessage();
    if (previous?.code === 'changed') notice(strings.changed);
    if (document.querySelector('form.cart-page, cart-drawer, quantity-selector[data-ff-gift-campaign]')) scheduleCheck();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once: true});
  else initialize();
})();

(() => {
  'use strict';
  if (window.FF_GIFT_DIALOG_QA20) return;
  window.FF_GIFT_DIALOG_QA20 = true;
  const popupSelector = '.salepify-popup';
  const closeSelector = '.salepify-popup-header__close-button';
  const claimSelector = '.salepify-popup__claim-button';
  const continueSelector = '.salepify-popup__keep-shopping-button';
  const german = (document.documentElement.lang || '').toLowerCase().startsWith('de');
  const labels = german ? {
    close: 'Geschenkauswahl schließen', dialog: 'Gratisgeschenk auswählen',
    search: 'Geschenke suchen', decrease: 'Anzahl verringern',
    increase: 'Anzahl erhöhen', quantity: 'Anzahl'
  } : {
    close: 'Close gift selection', dialog: 'Choose a free gift',
    search: 'Search gifts', decrease: 'Decrease quantity',
    increase: 'Increase quantity', quantity: 'Quantity'
  };
  const focusSelector = 'a[href],button,input,select,textarea,[tabindex]';
  let active = null;
  let previousFocus = null;
  let frame = 0;
  let nextId = 0;
  const background = new Map();
  const visible = el => !!el?.isConnected && el.getClientRects().length > 0 &&
    getComputedStyle(el).visibility !== 'hidden' && getComputedStyle(el).display !== 'none';
  const set = (el, name, value) => {
    if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
  };
  const focusable = el => visible(el) && !el.closest('[inert]') &&
    !el.matches(':disabled,[aria-disabled="true"]') && el.tabIndex >= 0;
  const controls = () => active ? [...active.querySelectorAll(focusSelector)].filter(focusable) : [];
  const focus = el => el?.focus({preventScroll: true});

  function enhance(popup) {
    set(popup, 'role', 'dialog');
    set(popup, 'aria-modal', 'true');
    set(popup, 'tabindex', '-1');
    const title = popup.querySelector('.salepify-popup-header__title');
    if (title) {
      if (!title.id) title.id = `ff-gift-dialog-title-${++nextId}`;
      set(popup, 'aria-labelledby', title.id);
    } else set(popup, 'aria-label', labels.dialog);
    const close = popup.querySelector(closeSelector);
    set(close, 'role', 'button');
    set(close, 'tabindex', '0');
    set(close, 'aria-label', labels.close);
    const continueButton = popup.querySelector(continueSelector);
    set(continueButton, 'role', 'button');
    set(continueButton, 'tabindex', '0');
    const claim = popup.querySelector(claimSelector);
    if (claim) {
      const off = claim.classList.contains('salepify-popup__claim-button--off');
      set(claim, 'role', 'button');
      set(claim, 'tabindex', off ? '-1' : '0');
      set(claim, 'aria-disabled', String(off));
    }
    const search = popup.querySelector('.salepify-popup__search-input');
    if (search && !search.getAttribute('aria-label')) set(search, 'aria-label', labels.search);
    const dismiss = popup.querySelector('.salepify-popup__dismiss-toggle input');
    const dismissText = popup.querySelector('.salepify-popup__dismiss-help-text')?.textContent.trim();
    if (dismiss && dismissText) set(dismiss, 'aria-label', dismissText);
    for (const [selector, text] of [
      ['.salepify-popup-item__decrease-btn', labels.decrease],
      ['.salepify-popup-item__increase-btn', labels.increase],
      ['.salepify-popup-item__qty-input', labels.quantity]
    ]) popup.querySelectorAll(selector).forEach(el => set(el, 'aria-label', text));
  }

  function isolate(popup) {
    let branch = popup;
    while (branch?.parentElement) {
      const parent = branch.parentElement;
      for (const sibling of parent.children) {
        if (sibling === branch || !(sibling instanceof HTMLElement) ||
            sibling.matches('script,style,link')) continue;
        if (!background.has(sibling)) background.set(sibling, sibling.inert);
        sibling.inert = true;
      }
      if (parent === document.body) break;
      branch = parent;
    }
  }

  function release() {
    const was = active;
    active = null;
    for (const [el, inert] of background) el.inert = inert;
    background.clear();
    if (!was) return;
    let target = previousFocus;
    previousFocus = null;
    if (!target || target === document.body || !visible(target) || target.closest('[inert]') || was.contains(target)) {
      const drawer = [...document.querySelectorAll('cart-drawer[open]')].find(visible);
      target = drawer?.querySelector('button') || document.querySelector('main h1') || document.querySelector('main');
    }
    if (!target || !visible(target)) return;
    if (target.tabIndex < 0) target.setAttribute('tabindex', '-1');
    focus(target);
  }

  function refresh() {
    frame = 0;
    const popup = [...document.querySelectorAll(`${popupSelector}--show`)].find(visible);
    if (!popup) { release(); return; }
    if (active !== popup) {
      release();
      previousFocus = document.activeElement;
      active = popup;
      enhance(popup);
      isolate(popup);
      focus(popup.querySelector(closeSelector) || popup);
    } else {
      enhance(popup);
      isolate(popup);
      if (!popup.contains(document.activeElement) || !visible(document.activeElement)) {
        focus(controls()[0] || popup);
      }
    }
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(refresh);
  }

  window.addEventListener('keydown', event => {
    if (!active || !visible(active)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      active.querySelector(closeSelector)?.click();
      schedule();
    } else if (event.key === 'Tab') {
      const items = controls();
      const first = items[0] || active;
      const last = items[items.length - 1] || active;
      if (!items.includes(document.activeElement) || !items.length ||
          (event.shiftKey && document.activeElement === first) ||
          (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        focus(event.shiftKey ? last : first);
      }
      event.stopImmediatePropagation();
    } else if ((event.key === 'Enter' || event.key === ' ') &&
        event.target instanceof Element && event.target.matches(`${closeSelector},${claimSelector},${continueSelector}`)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.target.getAttribute('aria-disabled') !== 'true') event.target.click();
    }
  }, true);

  window.addEventListener('focusin', event => {
    if (!active || !visible(active)) return;
    event.stopImmediatePropagation();
    if (!active.contains(event.target)) focus(controls()[0] || active);
  }, true);

  const observer = new MutationObserver(records => {
    if (active || records.some(record =>
      record.target instanceof Element && record.target.closest(popupSelector) ||
      [...record.addedNodes].some(node => node instanceof Element &&
        (node.matches(popupSelector) || node.querySelector(popupSelector))))) schedule();
  });
  function start() {
    observer.observe(document.body, {subtree: true, childList: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden']});
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
